import { useAuthActions } from "@convex-dev/auth/react";
import { useAction, useQuery } from "convex/react";
import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
} from "@repo/ui";
import { api } from "@convex/_generated/api";

/** Même seuil que la validation côté serveur (`convex/compte.ts`). */
const LONGUEUR_MDP_MIN = 8;

type Retour = { type: "ok" | "ko"; message: string } | null;

/**
 * Changement de mot de passe, deux voies :
 * 1. avec l'ancien mot de passe (action `compte.changerMonMotDePasse`) ;
 * 2. par code reçu par email (flows `reset` puis `reset-verification` du
 *    provider Password) — utile si l'ancien mot de passe est oublié.
 */
export function CarteMotDePasse() {
  const user = useQuery(api.auth.currentUser);
  const changerMotDePasse = useAction(api.compte.changerMonMotDePasse);
  const { signIn } = useAuthActions();

  const [ancien, setAncien] = useState("");
  const [nouveau, setNouveau] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [retour, setRetour] = useState<Retour>(null);

  // Parcours par email : `false` tant que le code n'a pas été demandé.
  const [codeDemande, setCodeDemande] = useState(false);
  const [code, setCode] = useState("");
  const [mdpParCode, setMdpParCode] = useState("");
  const [enCoursCode, setEnCoursCode] = useState(false);
  const [retourCode, setRetourCode] = useState<Retour>(null);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setRetour(null);
    if (nouveau !== confirmation) {
      setRetour({ type: "ko", message: "Les deux saisies diffèrent" });
      return;
    }
    setEnCours(true);
    try {
      await changerMotDePasse({
        ancienMotDePasse: ancien,
        nouveauMotDePasse: nouveau,
      });
      setAncien("");
      setNouveau("");
      setConfirmation("");
      setRetour({ type: "ok", message: "Mot de passe modifié." });
    } catch (err) {
      setRetour({
        type: "ko",
        message: err instanceof Error ? err.message : "Erreur inconnue",
      });
    } finally {
      setEnCours(false);
    }
  };

  const demanderCode = async () => {
    if (!user?.email) return;
    setRetourCode(null);
    setEnCoursCode(true);
    try {
      await signIn("password", { email: user.email, flow: "reset" });
      setCodeDemande(true);
      setRetourCode({
        type: "ok",
        message: `Code envoyé à ${user.email}. Il expire dans 15 minutes.`,
      });
    } catch (err) {
      setRetourCode({
        type: "ko",
        message: err instanceof Error ? err.message : "Envoi impossible",
      });
    } finally {
      setEnCoursCode(false);
    }
  };

  const validerCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) return;
    setRetourCode(null);
    setEnCoursCode(true);
    try {
      await signIn("password", {
        email: user.email,
        code,
        newPassword: mdpParCode,
        flow: "reset-verification",
      });
      setCode("");
      setMdpParCode("");
      setCodeDemande(false);
      setRetourCode({ type: "ok", message: "Mot de passe modifié." });
    } catch {
      // Message générique : ne pas distinguer « code faux » de « code expiré ».
      setRetourCode({ type: "ko", message: "Code invalide ou expiré" });
    } finally {
      setEnCoursCode(false);
    }
  };

  const formulaireIncomplet =
    ancien === "" || nouveau.length < LONGUEUR_MDP_MIN || confirmation === "";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Mot de passe</CardTitle>
        <CardDescription>
          {LONGUEUR_MDP_MIN} caractères minimum.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={(e) => void soumettre(e)} className="space-y-4">
          <div>
            <Label htmlFor="mdp-ancien" className="mb-1">
              Mot de passe actuel
            </Label>
            <Input
              id="mdp-ancien"
              type="password"
              autoComplete="current-password"
              value={ancien}
              onChange={(e) => setAncien(e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="mdp-nouveau" className="mb-1">
                Nouveau mot de passe
              </Label>
              <Input
                id="mdp-nouveau"
                type="password"
                autoComplete="new-password"
                value={nouveau}
                onChange={(e) => setNouveau(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="mdp-confirmation" className="mb-1">
                Confirmation
              </Label>
              <Input
                id="mdp-confirmation"
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </div>
          </div>

          {retour && (
            <p
              className={
                retour.type === "ko"
                  ? "text-sm text-destructive"
                  : "text-sm text-muted-foreground"
              }
            >
              {retour.message}
            </p>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={enCours || formulaireIncomplet}>
              {enCours ? "Modification…" : "Modifier le mot de passe"}
            </Button>
          </div>
        </form>

        <Separator />

        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Mot de passe actuel oublié ? Recevez un code par email pour en
              définir un nouveau.
            </p>
            <Button
              type="button"
              variant="outline"
              disabled={enCoursCode || !user?.email}
              onClick={() => void demanderCode()}
            >
              {codeDemande ? "Renvoyer un code" : "Recevoir un code par email"}
            </Button>
          </div>

          {/* Champs TOUJOURS rendus, jamais derrière un état local : un code
              reste valide 15 minutes, souvent le temps d'aller le lire dans sa
              boîte mail. Si le formulaire n'existait qu'après le clic sur
              « Recevoir un code », un simple rechargement de page le faisait
              disparaître avec un code valide en main, et plus rien n'indiquait
              où le saisir. */}
          <form onSubmit={(e) => void validerCode(e)} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="mdp-code" className="mb-1">
                  Code reçu par email
                </Label>
                <Input
                  id="mdp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="mdp-par-code" className="mb-1">
                  Nouveau mot de passe
                </Label>
                <Input
                  id="mdp-par-code"
                  type="password"
                  autoComplete="new-password"
                  value={mdpParCode}
                  onChange={(e) => setMdpParCode(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={
                  enCoursCode ||
                  code === "" ||
                  mdpParCode.length < LONGUEUR_MDP_MIN
                }
              >
                {enCoursCode ? "Validation…" : "Valider le code"}
              </Button>
            </div>
          </form>

          {retourCode && (
            <p
              className={
                retourCode.type === "ko"
                  ? "text-sm text-destructive"
                  : "text-sm text-muted-foreground"
              }
            >
              {retourCode.message}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
